import React, { useEffect, useState } from 'react';
import { LockIcon, CheckCircle2Icon, AlertCircleIcon, ClockIcon } from 'lucide-react';
import { LiveVisitorCounter } from './LiveVisitorCounter';
export function PricingSection() {
  const [timeLeft, setTimeLeft] = useState<{
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  useEffect(() => {
    // Cookie Helper Functions
    const setCookie = (name: string, value: string, days: number) => {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`;
    };
    const getCookie = (name: string) => {
      const nameEQ = name + '=';
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
      }
      return null;
    };
    // Initialize Timer Logic
    const initTimer = () => {
      const cookieName = 'offerExpiry';
      let expiryTime = getCookie(cookieName);
      if (!expiryTime) {
        // Set expiry to 4 hours from now
        const newExpiry = new Date().getTime() + 4 * 60 * 60 * 1000;
        setCookie(cookieName, newExpiry.toString(), 30);
        expiryTime = newExpiry.toString();
      }
      const calculateTimeLeft = () => {
        const now = new Date().getTime();
        const difference = parseInt(expiryTime!) - now;
        if (difference <= 0) {
          setIsExpired(true);
          setTimeLeft({
            hours: 0,
            minutes: 0,
            seconds: 0
          });
          return false; // Stop interval
        }
        setIsExpired(false);
        setTimeLeft({
          hours: Math.floor(difference % (1000 * 60 * 60 * 24) / (1000 * 60 * 60)),
          minutes: Math.floor(difference % (1000 * 60 * 60) / (1000 * 60)),
          seconds: Math.floor(difference % (1000 * 60) / 1000)
        });
        return true; // Continue interval
      };
      // Initial calculation
      calculateTimeLeft();
      setIsInitialized(true);
      // Set interval
      const timer = setInterval(() => {
        const shouldContinue = calculateTimeLeft();
        if (!shouldContinue) clearInterval(timer);
      }, 1000);
      return () => clearInterval(timer);
    };
    return initTimer();
  }, []);
  const handleCheckout = () => {
    if (window.fbq) {
      window.fbq('track', 'AddToCart');
    }
  };
  // Format numbers to always be 2 digits
  const formatTime = (num: number) => num.toString().padStart(2, '0');
  if (!isInitialized) return null; // Prevent hydration mismatch / flashing
  return <section id="comprar" className="py-20 md:py-32 bg-slate-950 relative border-t border-slate-800" data-id="element-137">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-900/10 rounded-full blur-[120px] pointer-events-none" data-id="element-138"></div>

      <div className="max-w-4xl mx-auto px-5 md:px-8 relative z-10" data-id="element-139">
        <div className="mb-10 flex justify-center" data-id="element-140">
          <LiveVisitorCounter data-id="element-141" />
        </div>

        <div className="text-center mb-12" data-id="element-142">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-143">
            No dejes que el beneficio de la duda se convierta en tu mayor
            arrepentimiento.
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto" data-id="element-144">
            Accede hoy mismo al sistema completo por una fracción de lo que te
            costaría un solo error de juicio.
          </p>
        </div>

        <div className="max-w-lg mx-auto bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-slate-700" data-id="element-145">
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-6 text-center border-b border-slate-700" data-id="element-146">
            <h3 className="text-2xl font-bold text-white mb-2 uppercase tracking-wide" data-id="element-147">
              Acceso Completo
            </h3>
            <p className="text-brand-400 font-medium" data-id="element-148">
              El Código de Micro-Señales + Hoja de Campo
            </p>
          </div>

          <div className="p-8" data-id="element-149">
            {!isExpired && timeLeft && <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-8 flex flex-col items-center justify-center" data-id="element-150">
                <div className="flex items-center text-red-400 font-semibold mb-2" data-id="element-151">
                  <ClockIcon className="h-5 w-5 mr-2" data-id="element-152" />
                  <span data-id="element-153">La oferta expira en:</span>
                </div>
                <div className="flex gap-3 text-2xl font-bold text-white" data-id="element-154">
                  <div className="flex flex-col items-center" data-id="element-155">
                    <span className="bg-slate-800 px-3 py-1 rounded" data-id="element-156">
                      {formatTime(timeLeft.hours)}
                    </span>
                    <span className="text-[10px] text-slate-400 mt-1 uppercase" data-id="element-157">
                      Horas
                    </span>
                  </div>
                  <span className="text-red-500 py-1" data-id="element-158">:</span>
                  <div className="flex flex-col items-center" data-id="element-159">
                    <span className="bg-slate-800 px-3 py-1 rounded" data-id="element-160">
                      {formatTime(timeLeft.minutes)}
                    </span>
                    <span className="text-[10px] text-slate-400 mt-1 uppercase" data-id="element-161">
                      Min
                    </span>
                  </div>
                  <span className="text-red-500 py-1" data-id="element-162">:</span>
                  <div className="flex flex-col items-center" data-id="element-163">
                    <span className="bg-slate-800 px-3 py-1 rounded" data-id="element-164">
                      {formatTime(timeLeft.seconds)}
                    </span>
                    <span className="text-[10px] text-slate-400 mt-1 uppercase" data-id="element-165">
                      Seg
                    </span>
                  </div>
                </div>
              </div>}

            <div className="flex justify-center items-end mb-8 gap-3" data-id="element-166">
              {!isExpired ? <>
                  <span className="text-slate-500 text-2xl line-through mb-1" data-id="element-167">
                    $47.99
                  </span>
                  <span className="text-5xl font-black text-white" data-id="element-168">
                    $11.99{' '}
                    <span className="text-xl text-slate-400 font-medium" data-id="element-169">
                      USD
                    </span>
                  </span>
                </> : <span className="text-5xl font-black text-white" data-id="element-170">
                  $47.99{' '}
                  <span className="text-xl text-slate-400 font-medium" data-id="element-171">
                    USD
                  </span>
                </span>}
            </div>

            <a href="https://pay.hotmart.com/B105540653I" onClick={handleCheckout} className="block w-full bg-brand-600 hover:bg-brand-500 text-white text-center font-bold px-6 py-5 rounded-xl text-xl transition-all duration-300 shadow-[0_0_20px_rgba(13,148,136,0.3)] transform hover:scale-105 active:scale-95 mb-6" data-id="element-172">
              DESCARGAR EL CÓDIGO AHORA
              <span className="block text-sm font-normal text-brand-100 mt-1" data-id="element-173">
                Recibe acceso instantáneo y empieza a ver lo que otros ignoran
              </span>
            </a>

            <div className="flex items-center justify-center text-slate-400 text-sm mb-8" data-id="element-174">
              <LockIcon className="h-4 w-4 mr-2" data-id="element-175" />
              <span data-id="element-176">Pago 100% seguro y encriptado</span>
            </div>

            <ul className="space-y-4" data-id="element-177">
              <li className="flex items-start" data-id="element-178">
                <CheckCircle2Icon className="h-5 w-5 text-brand-500 mr-3 flex-shrink-0" data-id="element-179" />
                <span className="text-slate-300 text-sm" data-id="element-180">
                  Descarga inmediata del Manual PDF
                </span>
              </li>
              <li className="flex items-start" data-id="element-181">
                <CheckCircle2Icon className="h-5 w-5 text-brand-500 mr-3 flex-shrink-0" data-id="element-182" />
                <span className="text-slate-300 text-sm" data-id="element-183">
                  Protocolo de 7 Micro-expresiones
                </span>
              </li>
              <li className="flex items-start" data-id="element-184">
                <CheckCircle2Icon className="h-5 w-5 text-brand-500 mr-3 flex-shrink-0" data-id="element-185" />
                <span className="text-slate-300 text-sm" data-id="element-186">
                  Hoja de Campo "Alerta Roja"
                </span>
              </li>
              <li className="flex items-start" data-id="element-187">
                <CheckCircle2Icon className="h-5 w-5 text-brand-500 mr-3 flex-shrink-0" data-id="element-188" />
                <span className="text-slate-300 text-sm" data-id="element-189">
                  Acceso de por vida a futuras actualizaciones
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>;
}